/* istanbul ignore file */
// libs
import React, { ReactElement, ReactNode } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import BreadcrumbsWithMenu from '../BreadcrumbsWithMenu/BreadcrumbsWithMenu';
// context
import { useShowContext } from 'framework/show';
// services
import { getVisibility } from 'framework/utils';
import {
  getShowPageContentInnerSx,
  showPageEntityHeaderWrapperSx,
  showPageHeaderWrapperSx,
  showPagePaperSx,
  showPageTopButtonsSx,
} from '../pageLayoutSx';
import { topButtonsSx } from './topButtonsSx';

export interface ShowPageLayoutWithoutMenuProps {
  breadcrumbs?: React.ReactNode;
  children: ReactNode;
  entityHeader?: Nullable<ReactElement>;
  menu?: ReactElement;
  buttons?: ReactElement[];
  breadcrumbsButtons?: ReactElement[];
  headerButtons?: ReactElement[];
  hasSeparateSections?: boolean;
  minHeight?: string;
}

interface ShowPageLayoutProps extends ShowPageLayoutWithoutMenuProps {
  menu?: ReactElement;
}

const getButtons = (data: any, buttons?: ReactElement[]) =>
  buttons &&
  buttons.map(button => {
    const { isVisible: isVisibleProp } = button.props;

    const isVisible = getVisibility(data, isVisibleProp);

    return isVisible ? button : null;
  });

const ShowPageLayout = ({
  breadcrumbs,
  menu,
  buttons,
  children,
  entityHeader,
  headerButtons,
  breadcrumbsButtons,
  hasSeparateSections,
  minHeight,
}: ShowPageLayoutProps) => {
  const { data } = useShowContext();

  return (
    <Paper elevation={0} sx={showPagePaperSx}>
      <Box
        sx={{
          display: { xs: 'block', md: 'flex' },
          justifyContent: { md: 'space-between' },
          alignItems: { md: 'flex-start' },
        }}
      >
        <BreadcrumbsWithMenu breadcrumbs={breadcrumbs} menu={menu} />
        <Box display="flex" gap="5px">
          {breadcrumbsButtons}
        </Box>
      </Box>

      {(entityHeader || headerButtons) && (
        <Box sx={showPageHeaderWrapperSx}>
          <Box sx={showPageEntityHeaderWrapperSx}>{entityHeader}</Box>
          <Stack sx={{ ...topButtonsSx, ...showPageTopButtonsSx }}>{getButtons(data, headerButtons)}</Stack>
        </Box>
      )}
      <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
        {menu && (
          <Box
            sx={{
              mb: '25px',
              ml: { xs: -2, md: '-19px' },
              mr: { xs: -2, md: '35px' },
              flexShrink: 0,
              width: { md: '215px' },
              display: { xs: 'none', md: 'block' },
            }}
          >
            {menu}
          </Box>
        )}

        <Box data-hj-suppress sx={getShowPageContentInnerSx(hasSeparateSections, minHeight)}>
          {buttons && (
            <Box sx={{ zIndex: 1, position: 'relative', display: { md: 'flex' }, justifyContent: { md: 'flex-end' } }}>
              <Stack
                sx={{
                  ...topButtonsSx,
                  ...{
                    display: 'flex',
                    '&:not(:empty)': {
                      mb: { xs: 4, md: -4 },
                      mt: { xs: 1, md: 0 },
                      display: 'flex',
                      justifyContent: { md: 'flex-end' },
                    },
                  },
                }}
              >
                {getButtons(data, buttons)}
              </Stack>
            </Box>
          )}
          {React.Children.map(children, child => {
            const { isVisible: isVisibleProp } = (child as ReactElement).props;

            const isVisible = getVisibility(data, isVisibleProp);

            return isVisible ? child : null;
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default ShowPageLayout;
